import { useState, useEffect } from 'react';
import { X, Package, DollarSign, AlertTriangle, Barcode, Tag } from 'lucide-react';
import { inventoryApi, type ProductCreateRequest, type ProductResponse, type LocationResponse } from '../../api/inventoryApi';
import { supplierApi } from '../../api/supplierApi';
import type { Supplier } from '../../api/supplierApi';
import SearchableSelect from '../../components/ui/SearchableSelect';

interface ProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    salonId: string;
    locations: LocationResponse[];
    product?: ProductResponse | null; // For editing
}

const ProductModal = ({ isOpen, onClose, onSuccess, salonId, locations, product }: ProductModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [sku, setSku] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [unit, setUnit] = useState('unidad');
    const [costPrice, setCostPrice] = useState('');
    const [salePrice, setSalePrice] = useState('');
    const [minStockAlert, setMinStockAlert] = useState('5');
    const [barcode, setBarcode] = useState('');
    const [productType, setProductType] = useState<'SALE' | 'SUPPLY'>('SUPPLY');

    // Supplier state
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [selectedSupplierId, setSelectedSupplierId] = useState('');

    // Initial stock (only for new products)
    const [initialStock, setInitialStock] = useState('');
    const [initialLocation, setInitialLocation] = useState('');

    const isEditing = !!product;

    // Load suppliers
    useEffect(() => {
        if (!salonId) return;
        const loadSuppliers = async () => {
            try {
                const data = await supplierApi.getSalonSuppliers(salonId);
                setSuppliers(data.filter(s => s.isActive));
            } catch (err) {
                console.error('Error loading suppliers:', err);
            }
        };
        loadSuppliers();
    }, [salonId]);

    // Pre-fill form when editing
    useEffect(() => {
        if (product) {
            setSku(product.sku);
            setName(product.name);
            setDescription(product.description || '');
            setCategory(product.category || '');
            setSelectedSupplierId(product.supplierId || '');
            setUnit(product.unit || 'unidad');
            setCostPrice(product.costPrice?.toString() || '');
            setSalePrice(product.salePrice?.toString() || '');
            setMinStockAlert(product.minStockAlert?.toString() || '5');
            setBarcode(product.barcode || '');
            // Note: productType will need to be added to ProductResponse when backend supports it
            // setProductType(product.productType || 'SUPPLY');
        } else {
            // Reset form for new product
            setSku('');
            setName('');
            setDescription('');
            setCategory('');
            setSelectedSupplierId('');
            setUnit('unidad');
            setCostPrice('');
            setSalePrice('');
            setMinStockAlert('5');
            setBarcode('');
            setProductType('SUPPLY');
            setInitialStock('');
            setInitialLocation(locations.length > 0 ? locations[0].id : '');
        }
    }, [product, locations]);

    // Auto-generate SKU from name
    const generateSku = () => {
        if (!name) return;
        const prefix = name.split(' ')
            .map(word => word.charAt(0).toUpperCase())
            .join('')
            .slice(0, 3);
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        setSku(`${prefix}-${random}`);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const request: ProductCreateRequest = {
                sku,
                name,
                description: description || undefined,
                category: category || undefined,
                supplierId: selectedSupplierId || undefined,
                unit,
                costPrice: costPrice ? parseFloat(costPrice) : undefined,
                salePrice: salePrice ? parseFloat(salePrice) : undefined,
                minStockAlert: minStockAlert ? parseInt(minStockAlert) : undefined,
                barcode: barcode || undefined,
            };

            if (isEditing && product) {
                await inventoryApi.updateProduct(product.id, request);
            } else {
                const newProduct = await inventoryApi.createProduct(salonId, request);

                // Add initial stock if specified
                if (initialStock && initialLocation && parseInt(initialStock) > 0) {
                    await inventoryApi.addStock(salonId, {
                        productId: newProduct.id,
                        locationId: initialLocation,
                        quantity: parseInt(initialStock),
                        notes: 'Stock inicial'
                    });
                }
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Error saving product:', err);
            setError(err.response?.data?.message || 'Error al guardar el producto');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-white">
                                {isEditing ? 'Editar Producto' : 'Nuevo Producto'}
                            </h2>
                            <p className="text-blue-100 text-sm">
                                {isEditing ? 'Actualiza la información del producto' : 'Agrega un nuevo producto al inventario'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            {error}
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Basic Info Section */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <Tag className="w-4 h-4 text-blue-500" />
                                Información Básica
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* SKU */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        SKU (Código) *
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={sku}
                                            onChange={(e) => setSku(e.target.value.toUpperCase())}
                                            placeholder="ABC-001"
                                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                                            required
                                            disabled={isEditing}
                                        />
                                        {!isEditing && (
                                            <button
                                                type="button"
                                                onClick={generateSku}
                                                disabled={!name.trim()}
                                                className={`px-3 py-2 rounded-lg text-sm transition-colors ${name.trim()
                                                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer'
                                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    }`}
                                                title={name.trim() ? 'Generar SKU automático basado en el nombre' : 'Primero ingresa el nombre del producto'}
                                            >
                                                Auto
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre del Producto *
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Ej: Shampoo Profesional 500ml"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                {/* Category */}
                                <SearchableSelect
                                    label="Categoría"
                                    placeholder="Seleccionar categoría..."
                                    options={[
                                        { value: "", label: "Seleccionar categoría..." },
                                        { value: "Tintes", label: "Tintes", group: "Productos para el Cabello" },
                                        { value: "Shampoos", label: "Shampoos", group: "Productos para el Cabello" },
                                        { value: "Acondicionadores", label: "Acondicionadores", group: "Productos para el Cabello" },
                                        { value: "Tratamientos", label: "Tratamientos", group: "Productos para el Cabello" },
                                        { value: "Mascarillas", label: "Mascarillas", group: "Productos para el Cabello" },
                                        { value: "Aceites", label: "Aceites", group: "Productos para el Cabello" },
                                        { value: "Styling", label: "Productos de Styling", group: "Productos para el Cabello" },
                                        { value: "Herramientas", label: "Herramientas", group: "Herramientas y Equipos" },
                                        { value: "Equipos", label: "Equipos", group: "Herramientas y Equipos" },
                                        { value: "Accesorios", label: "Accesorios", group: "Herramientas y Equipos" },
                                        { value: "Consumibles", label: "Consumibles", group: "Consumibles" },
                                        { value: "Desechables", label: "Desechables", group: "Consumibles" },
                                        { value: "Retail", label: "Productos Retail", group: "Para la Venta" },
                                        { value: "Kits", label: "Kits y Promociones", group: "Para la Venta" },
                                        { value: "Otros", label: "Otros" }
                                    ]}
                                    value={category}
                                    onChange={setCategory}
                                />

                                {/* Supplier */}
                                <SearchableSelect
                                    label="Proveedor"
                                    placeholder="Seleccionar proveedor..."
                                    options={[
                                        { value: "", label: "Seleccionar proveedor..." },
                                        ...suppliers.map(sup => ({ value: sup.id, label: sup.name }))
                                    ]}
                                    value={selectedSupplierId}
                                    onChange={setSelectedSupplierId}
                                />

                                {/* Unit */}
                                <SearchableSelect
                                    label="Unidad de Medida"
                                    placeholder="Seleccionar unidad..."
                                    options={[
                                        { value: "unidad", label: "Unidad" },
                                        { value: "ml", label: "Mililitros (ml)" },
                                        { value: "gr", label: "Gramos (gr)" },
                                        { value: "frasco", label: "Frasco" },
                                        { value: "tubo", label: "Tubo" },
                                        { value: "caja", label: "Caja" },
                                        { value: "paquete", label: "Paquete" },
                                    ]}
                                    value={unit}
                                    onChange={setUnit}
                                />
                            </div>

                            {/* Description */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Descripción
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Descripción detallada del producto..."
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                />
                            </div>

                            {/* Product Type */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tipo de Producto
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setProductType('SUPPLY')}
                                        className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${productType === 'SUPPLY'
                                            ? 'border-blue-500 bg-blue-50 shadow-md'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        <span className="text-2xl">🧰</span>
                                        <span className={`text-sm font-medium ${productType === 'SUPPLY' ? 'text-blue-700' : 'text-gray-600'}`}>
                                            Insumo / Utilería
                                        </span>
                                        <span className="text-xs text-gray-400 text-center">
                                            Uso interno del salón
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setProductType('SALE')}
                                        className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${productType === 'SALE'
                                            ? 'border-emerald-500 bg-emerald-50 shadow-md'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        <span className="text-2xl">🛒</span>
                                        <span className={`text-sm font-medium ${productType === 'SALE' ? 'text-emerald-700' : 'text-gray-600'}`}>
                                            Para la Venta
                                        </span>
                                        <span className="text-xs text-gray-400 text-center">
                                            Se vende a clientes
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Pricing Section */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <DollarSign className="w-4 h-4 text-green-500" />
                                Precios
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Precio de Costo
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                        <input
                                            type="number"
                                            value={costPrice}
                                            onChange={(e) => setCostPrice(e.target.value)}
                                            placeholder="0"
                                            min="0"
                                            step="100"
                                            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Lo que pagas al proveedor</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Precio de Venta
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                        <input
                                            type="number"
                                            value={salePrice}
                                            onChange={(e) => setSalePrice(e.target.value)}
                                            placeholder="0"
                                            min="0"
                                            step="100"
                                            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Lo que cobras al cliente</p>
                                </div>
                            </div>

                            {/* Profit margin indicator */}
                            {costPrice && salePrice && parseFloat(salePrice) > 0 && (
                                <div className="mt-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Margen de ganancia:</span>
                                        <span className="font-bold text-green-600">
                                            {(((parseFloat(salePrice) - parseFloat(costPrice)) / parseFloat(salePrice)) * 100).toFixed(1)}%
                                        </span>
                                    </div>
                                    <div className="mt-2 w-full bg-green-100 rounded-full h-2">
                                        <div
                                            className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full transition-all"
                                            style={{ width: `${Math.min(((parseFloat(salePrice) - parseFloat(costPrice)) / parseFloat(salePrice)) * 100, 100)}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Initial Stock (only for new products) - moved up for better visibility */}
                        {!isEditing && (
                            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                                <h3 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                                    <Package className="w-4 h-4" />
                                    Stock Inicial (Opcional)
                                </h3>
                                {locations.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <SearchableSelect
                                            label="Ubicación"
                                            placeholder="Seleccionar ubicación..."
                                            options={locations.map(loc => ({
                                                value: loc.id,
                                                label: `${loc.name} ${loc.isDefault ? '(Principal)' : ''}`
                                            }))}
                                            value={initialLocation}
                                            onChange={setInitialLocation}
                                        />
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Cantidad Inicial
                                            </label>
                                            <input
                                                type="number"
                                                value={initialStock}
                                                onChange={(e) => setInitialStock(e.target.value)}
                                                placeholder="0"
                                                min="0"
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-sm text-blue-800 bg-blue-100/50 p-3 rounded-lg flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-medium">No tienes ubicaciones creadas</p>
                                            <p className="mt-1 text-blue-700/80">
                                                Para agregar stock, primero necesitas crear una ubicación (ej: Bodega, Estante A) en la pantalla principal de inventario.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Stock Alert Section */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                                Alerta de Stock
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Stock Mínimo para Alerta
                                    </label>
                                    <input
                                        type="number"
                                        value={minStockAlert}
                                        onChange={(e) => setMinStockAlert(e.target.value)}
                                        placeholder="5"
                                        min="0"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Recibe alerta cuando el stock baje de este número</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                                        <Barcode className="w-4 h-4" />
                                        Código de Barras
                                    </label>
                                    <input
                                        type="text"
                                        value={barcode}
                                        onChange={(e) => setBarcode(e.target.value)}
                                        placeholder="Opcional"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            disabled={isLoading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Package className="w-4 h-4" />
                                    {isEditing ? 'Actualizar' : 'Crear Producto'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductModal;
